/**
 * API service for memory/profile management endpoints.
 */
import api from './api';
import type {
  TeacherProfile,
  ProfileStatus,
  InitializeProfileRequest,
  InitializeProfileResponse,
  UpdateProfileRequest,
  AddExampleRequest,
  AddExampleResponse,
  RemoveExampleResponse,
  ReExtractResponse,
} from '../types/memory';

/**
 * Get a teacher's profile by ID.
 */
export async function getTeacherProfile(teacherId: string): Promise<TeacherProfile> {
  const response = await api.get<TeacherProfile>(`/api/memory/teacher/${teacherId}`);
  return response.data;
}

/**
 * Check if a teacher has an initialized profile.
 */
export async function getTeacherProfileStatus(teacherId: string): Promise<ProfileStatus> {
  const response = await api.get<ProfileStatus>(`/api/memory/teacher/${teacherId}/status`);
  return response.data;
}

/**
 * Initialize a teacher profile from feedback examples.
 */
export async function initializeTeacherProfile(
  request: InitializeProfileRequest
): Promise<InitializeProfileResponse> {
  const response = await api.post<InitializeProfileResponse>(
    '/api/memory/teacher/initialize',
    request
  );
  return response.data;
}

/**
 * Update a teacher profile's fields.
 */
export async function updateTeacherProfile(
  teacherId: string,
  updates: UpdateProfileRequest
): Promise<TeacherProfile> {
  const response = await api.put<TeacherProfile>(`/api/memory/teacher/${teacherId}`, updates);
  return response.data;
}

/**
 * Add a feedback example to the teacher's corpus.
 */
export async function addFeedbackExample(
  teacherId: string,
  text: string,
  category: string = 'general'
): Promise<AddExampleResponse> {
  const request: AddExampleRequest = { text, category };
  const response = await api.post<AddExampleResponse>(
    `/api/memory/teacher/${teacherId}/examples`,
    request
  );
  return response.data;
}

/**
 * Remove a feedback example from the corpus by index.
 */
export async function removeFeedbackExample(
  teacherId: string,
  index: number
): Promise<RemoveExampleResponse> {
  const response = await api.delete<RemoveExampleResponse>(
    `/api/memory/teacher/${teacherId}/examples/${index}`
  );
  return response.data;
}

/**
 * Re-extract style profile from the current feedback corpus.
 */
export async function reExtractStyle(teacherId: string): Promise<ReExtractResponse> {
  const response = await api.post<ReExtractResponse>(
    `/api/memory/teacher/${teacherId}/re-extract`
  );
  return response.data;
}
